import { Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ROOT } from 'configs/routeNames';

export type ErrorFallbackProps = {
  error: unknown;
  resetError: () => void;
  compact?: boolean;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Something unexpected happened.';
};

export function ErrorFallback({ error, resetError, compact = false }: Readonly<ErrorFallbackProps>) {
  const navigate = useNavigate();
  const message = getErrorMessage(error);

  const goHome = () => {
    resetError();
    navigate(ROOT);
  };

  const reload = () => globalThis.location.reload();

  if (compact) {
    return (
      <div
        className="font-figtree flex flex-col items-center justify-center gap-3 rounded-lg border border-red-200 bg-red-50/50 p-6"
        role="alert"
      >
        <p className="text-sm font-medium text-red-800">Something went wrong</p>
        <p className="text-xs text-red-700 break-words max-w-md">{message}</p>
        <div className="flex gap-2">
          <Button size="small" onClick={resetError} type="primary">
            Try again
          </Button>
          <Button size="small" onClick={goHome}>
            Go to home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="font-figtree flex min-h-[320px] flex-col items-center justify-center gap-6 rounded-xl border border-red-200 bg-red-50/40 p-8 text-center"
      role="alert"
    >
      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-gray-900">Something went wrong</h2>
        <p className="max-w-md text-sm text-gray-600 break-words">{message}</p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <Button type="primary" onClick={resetError} size="large">
          Try again
        </Button>
        <Button onClick={goHome} size="large">
          Go to home
        </Button>
        <Button onClick={reload} size="large">
          Reload page
        </Button>
      </div>
    </div>
  );
}
